import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Drawer,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  useMediaQuery,
  Collapse,
  ListItemButton,
} from '@mui/material';
import {
  Home as HomeIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  PersonRemove as PersonRemoveIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Assessment as AssessmentIcon,
  BorderColor as SignatureIcon,
  PhotoLibrary as PhotoLibraryIcon,
  ReceiptLong as ReceiptLongIcon,
  FactCheck as FactCheckIcon,
  SpaceDashboard as SpaceDashboardIcon,
  AccountBalance as AccountBalanceIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Payments as PaymentsIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_URL } from '../config/api';
import logo from '../assets/di-logo.jpg';
import MyNotificationsBell from '../components/MyNotificationsBell';

const drawerWidth = 250;

const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [checkingManager, setCheckingManager] = useState(true);
  const [expensesOpen, setExpensesOpen] = useState(false);
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, getAuthHeaders } = useAuth();

  useEffect(() => {
    checkIfManager();
  }, [user]);

  const checkIfManager = async () => {
    if (!user || !user.id) {
      setCheckingManager(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/user-hierarchy/user-hierarchy/${user.id}`, {
        headers: getAuthHeaders()
      });
      // Check if user has direct reports
      const hasDirectReports = response.data?.directReportsCount > 0 || 
                               (response.data?.teamMembers && response.data.teamMembers.length > 0);
      setIsManager(hasDirectReports);
    } catch (error) {
      console.error('Error checking if user is manager:', error);
      setIsManager(false);
    } finally {
      setCheckingManager(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed. Please try again.');
    }
  };

  if (!user) return <div>Loading...</div>;

  // Menu items for all authenticated users
  const baseMenuItems = [
    { text: 'Dashboard', icon: <HomeIcon />, path: '/dashboard' },
    { text: 'Onboarding', icon: <PersonAddIcon />, path: '/onboarding' },
    { text: 'Existing Employee', icon: <PersonIcon />, path: '/existing' },
    { text: 'Offboarding', icon: <PersonRemoveIcon />, path: '/offboarding' },
    { text: 'Performance Review', icon: <AssessmentIcon />, path: '/performance-review' },
    { text: 'Digital Library', icon: <PhotoLibraryIcon />, path: '/digital-library' },
    { text: 'Generate Signature', icon: <SignatureIcon />, path: '/signature-generator' },
  ];

  // Add Manager Reviews only if user is a manager
  const managerMenuItems = isManager
    ? [...baseMenuItems,
        { text: 'Manager Reviews', icon: <AssessmentIcon />, path: '/manager/reviews' },
        { text: 'KPI Proposals', icon: <AssessmentIcon />, path: '/manager/kpi-proposals' },
      ]
    : baseMenuItems;

  // Employee expense entries are shown to every employee. Accounts entries need
  // AccessProvisioning.expenseAccounts; Expense Approvals needs expenseAccounts / expenseApprover
  const menuItems = managerMenuItems;

  // Grouped under the collapsible "Employee Expenses" menu
  const expenseSubItems = [
    { text: 'Expense Dashboard', icon: <SpaceDashboardIcon />, path: '/expense-dashboard' },
    { text: 'My Expenses', icon: <ReceiptLongIcon />, path: '/expenses' },
    { text: 'My Wallet', icon: <AccountBalanceWalletIcon />, path: '/expense-wallet' },
    ...(user.access?.expenseAccounts || user.access?.expenseApprover
      ? [{ text: 'Expense Approvals', icon: <FactCheckIcon />, path: '/expense-approvals' }] : []),
    ...(user.access?.expenseAccounts ? [
      { text: 'Accounts Expenses', icon: <AccountBalanceIcon />, path: '/expense-accounts' },
      { text: 'Expense Wallets', icon: <AccountBalanceWalletIcon />, path: '/expense-wallets' },
      { text: 'Accounts Reports', icon: <AssessmentIcon />, path: '/expense-accounts/reports' },
    ] : []),
    ...(user.access?.expenseAccounts ? [
      { text: 'Expense Masters', icon: <ReceiptLongIcon />, path: '/expense-masters' },
    ] : []),
  ];

  const isExpenseItemSelected = expenseSubItems.some(item => location.pathname === item.path);

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          py: 3,
          px: 2,
          textAlign: 'center',
          bgcolor: 'transparent',
          borderBottom: '1px solid rgba(255,255,255,0.12)',
        }}
      >
       {!isMobile && (
        <>
        {/* Logo above the title */}
        <Box
        component="img"
        src={logo}
        alt="Company Logo"
        sx={{
        height: 60,       // reduced size
        width: 'auto',
        mb: 0.3,            // margin bottom
        objectFit: 'contain',
        maxWidth: '80%'
          // ensure it doesn't overflow
        }}
        />
        <Typography
        variant="h5"
        sx={{ fontWeight: 700, color: '#fff', letterSpacing: 0.3, mb: 0.3,fontSize: '1.4rem' }}
        >
        Dive-In Quonnect
        </Typography>
        </>
      )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
            {user.email}
          </Typography>
          <MyNotificationsBell color="#fff" />
        </Box>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />

      <List>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => {
              if (item.customAction) {
                item.customAction();
                if (isMobile) setMobileOpen(false);
              } else if (item.path) {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }
            }}
            selected={item.path ? location.pathname === item.path : false}
            sx={{
              borderRadius: 2,
              mb: 2,
              bgcolor: (item.path && location.pathname === item.path) ? 'primary.light' : 'transparent',
              color: 'primary.contrastText',
              fontWeight: (item.path && location.pathname === item.path) ? 700 : 500,
              '&:hover': {
                bgcolor: 'primary.dark',
                color: 'primary.contrastText',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                  {item.text}
                </Typography>
              }
            />
          </ListItem>
        ))}

        {/* Employee Expenses Dropdown */}
        {expenseSubItems.length > 0 && (
          <>
            <ListItemButton
              onClick={() => setExpensesOpen(prev => !prev)}
              sx={{
                borderRadius: 2,
                mb: 2,
                bgcolor: isExpenseItemSelected ? 'primary.light' : 'transparent',
                color: 'primary.contrastText',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  color: 'primary.contrastText',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                <PaymentsIcon />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                    Employee Expenses
                  </Typography>
                }
              />
              {(expensesOpen || isExpenseItemSelected) ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>

            <Collapse in={expensesOpen || isExpenseItemSelected} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {expenseSubItems.map((item) => (
                  <ListItemButton
                    key={item.text}
                    onClick={() => {
                      navigate(item.path);
                      if (isMobile) setMobileOpen(false);
                    }}
                    selected={location.pathname === item.path}
                    sx={{
                      pl: 4,
                      borderRadius: 2,
                      mb: 1,
                      ml: 2,
                      mr: 2,
                      bgcolor: location.pathname === item.path ? 'primary.light' : 'transparent',
                      color: 'primary.contrastText',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                        color: 'primary.contrastText',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.text}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </>
        )}

        <ListItem
          button
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            mb: 2,
            bgcolor: 'transparent',
            color: 'primary.contrastText',
            fontWeight: 500,
            '&:hover': {
              bgcolor: 'error.main',
              color: 'error.contrastText',
            },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                Logout
              </Typography>
            }
          />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar for Mobile */}
      {isMobile && (
        <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1, bgcolor: 'primary.main' }}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div">
              Dive-In Quonnect
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Drawer */}
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              background: 'linear-gradient(180deg, #7e57c2 0%, #4a327d 100%)',
              borderRight: 'none',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: '100vh',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: isMobile ? 8 : 0, // Add top margin for AppBar on mobile
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
